import React, { useState, useEffect } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useToast } from '../../components/ui/Toast';
import Shell from '../dashboard/Shell';
import SecureBankAccountDisplay from '../../components/SecureBankAccountDisplay';
import PasswordConfirmModal from '../../components/PasswordConfirmModal';
import ChangeBankAccountModal from '../../components/ChangeBankAccountModal';
import KYCWizard from '../../components/KYCWizard';
import {
  Shield, CheckCircle, XCircle, Clock, DollarSign,
  CreditCard, TrendingUp, Users, FileText, AlertCircle,
  Building, Calendar
} from 'lucide-react';
import './MerchantKYC.css';

const MerchantKYC = () => {
  const { getAxios } = useSecureAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [kycStatus, setKycStatus] = useState(null);
  const [accountHistory, setAccountHistory] = useState([]);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showChangeBankModal, setShowChangeBankModal] = useState(false);
  const [passwordConfirmResolve, setPasswordConfirmResolve] = useState(null);

  useEffect(() => {
    fetchKYCStatus();
    fetchAccountHistory();
  }, []);

  const fetchKYCStatus = async () => {
    try {
      setLoading(true);
      const axios = getAxios();
      const response = await axios.get('/kyc/status');

      if (response.data.success) {
        setKycStatus(response.data.kyc);
      }
    } catch (error) {
      console.error('Error fetching KYC status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountHistory = async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/kyc/bank-account-history');

      if (response.data.success) {
        setAccountHistory(response.data.history);
      }
    } catch (error) {
      console.error('Error fetching account history:', error);
    }
  };

  const handlePasswordConfirmation = () => {
    return new Promise((resolve) => {
      setPasswordConfirmResolve(() => resolve);
      setShowPasswordModal(true);
    });
  };

  const handlePasswordConfirm = async (password) => {
    try {
      const axios = getAxios();
      const response = await axios.post('merchant/auth/verify-password', { password });
      return { success: response.data.success };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Failed to verify password' };
    }
  };

  const handlePasswordModalClose = (success) => {
    setShowPasswordModal(false);
    if (passwordConfirmResolve) {
      passwordConfirmResolve(success);
      setPasswordConfirmResolve(null);
    }
  };

  const handleChangeBankAccountSuccess = async () => {
    await fetchKYCStatus();
    await fetchAccountHistory();
  };

  const handleResubmitKYC = async () => {
    try {
      const axios = getAxios();
      const response = await axios.post('/kyc/resubmit');

      if (response.data.success) {
        await fetchKYCStatus();
        toast.success('KYC resubmitted successfully! Your application is now pending review.');
      } else {
        toast.error(response.data.error || 'Failed to resubmit KYC');
      }
    } catch (error) {
      console.error('Error resubmitting KYC:', error);
      toast.error(error.response?.data?.error || 'Failed to resubmit KYC. Please try again.');
    }
  };

  const getLevelInfo = (level) => {
    const levels = {
      LEVEL_0: {
        name: 'Level 0',
        description: 'Basic verification required',
        class: 'level-0'
      },
      LEVEL_1: {
        name: 'Level 1',
        description: 'Identity verified',
        class: 'level-1'
      },
      LEVEL_2: {
        name: 'Level 2',
        description: 'Business verified',
        class: 'level-2'
      },
      LEVEL_3: {
        name: 'Level 3',
        description: 'Premium verified',
        class: 'level-3'
      }
    };
    return levels[level] || levels.LEVEL_0;
  };

  const getStatusBadge = (status) => {
    const statuses = {
      APPROVED: { text: 'Approved', class: 'approved' },
      PENDING_REVIEW: { text: 'Pending Review', class: 'pending' },
      REJECTED: { text: 'Rejected', class: 'rejected' },
      NOT_STARTED: { text: 'Not Started', class: 'not-started' }
    };
    return statuses[status] || statuses.NOT_STARTED;
  };

  const getCapabilities = (kyc) => {
    if (!kyc) return [];

    const capabilities = [
      {
        title: 'Receive Payments',
        description: 'Accept payments from customers',
        enabled: kyc.canReceivePayments,
        icon: DollarSign
      },
      {
        title: 'Withdraw Funds',
        description: 'Transfer earnings to your bank',
        enabled: kyc.canWithdrawFunds,
        icon: CreditCard
      },
      {
        title: 'Daily Limit',
        description: kyc.dailyTransactionLimit ? `₦${parseFloat(kyc.dailyTransactionLimit).toLocaleString()}` : 'No limit set',
        enabled: kyc.dailyTransactionLimit > 0,
        icon: TrendingUp
      },
      {
        title: 'Monthly Limit',
        description: kyc.monthlyTransactionLimit ? `₦${parseFloat(kyc.monthlyTransactionLimit).toLocaleString()}` : 'No limit set',
        enabled: kyc.monthlyTransactionLimit > 0,
        icon: Calendar
      }
    ];

    return capabilities;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getChangeTypeLabel = (type) => {
    const types = {
      INITIAL_SETUP: 'Initial Setup',
      UPDATE: 'Account Updated',
      VERIFICATION: 'Account Verified',
      RE_VERIFICATION: 'Re-verification'
    };
    return types[type] || type;
  };

  if (loading) {
    return (
      <Shell>
        <div className="kyc-page">
          <div className="loading-spinner">
            <div className="spinner"></div>
          </div>
        </div>
      </Shell>
    );
  }

  if (!kycStatus) {
    return (
      <Shell>
        <div className="kyc-page">
          <div className="empty-state">
            <AlertCircle size={48} />
            <p>Unable to load KYC status. Please try again later.</p>
          </div>
        </div>
      </Shell>
    );
  }

  const levelInfo = getLevelInfo(kycStatus.level);
  const statusBadge = getStatusBadge(kycStatus.status);
  const capabilities = getCapabilities(kycStatus);

  return (
    <Shell>
      <div className="kyc-page">
        <div className="kyc-header">
          <h1>KYC Verification</h1>
          <p>Manage your verification status and bank account details</p>
        </div>

        {/* KYC Level Card */}
        <div className="kyc-level-card">
          <div className="level-header">
            <div className="level-info">
              <div className={`level-badge ${levelInfo.class}`}>
                <div className="level-badge-number">{kycStatus.level.replace('LEVEL_', '')}</div>
                <div className="level-badge-text">{levelInfo.name}</div>
              </div>
              <div className="level-details">
                <h2>{levelInfo.description}</h2>
                <p>Your account verification level determines your platform capabilities</p>
              </div>
            </div>
            <span className={`status-badge ${statusBadge.class}`}>
              {statusBadge.text}
            </span>
          </div>

          {/* Capabilities Grid */}
          <div className="capabilities-grid">
            {capabilities.map((capability, index) => (
              <div key={index} className={`capability-item ${capability.enabled ? 'enabled' : 'disabled'}`}>
                <div className={`capability-icon ${capability.enabled ? 'enabled' : 'disabled'}`}>
                  {capability.enabled ? <CheckCircle size={12} /> : <XCircle size={12} />}
                </div>
                <div className="capability-text">
                  <strong>{capability.title}</strong>
                  <span>{capability.description}</span>
                </div>
              </div>
            ))}
          </div>

          {kycStatus.status === 'APPROVED' && (kycStatus.level === 'LEVEL_1' || kycStatus.level === 'LEVEL_2' || kycStatus.level === 'LEVEL_0') && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: '#DBEAFE',
              border: '1px solid #60A5FA',
              borderRadius: '8px',
              color: '#1E40AF',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <TrendingUp size={18} />
                <strong>Upgrade Your KYC Level</strong>
              </div>
              <p style={{ margin: '0 0 8px 0' }}>
                {kycStatus.level === 'LEVEL_0' && 'Complete Level 1 KYC to start receiving payments up to ₦100,000 daily.'}
                {kycStatus.level === 'LEVEL_1' && 'Upgrade to Level 2 to increase your daily limit to ₦500,000.'}
                {kycStatus.level === 'LEVEL_2' && 'Upgrade to Level 3 for unlimited transactions.'}
              </p>
            </div>
          )}

          {kycStatus.status === 'REJECTED' && kycStatus.rejectionReason && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: '#FEE2E2',
              border: '1px solid #FCA5A5',
              borderRadius: '8px',
              color: '#991B1B',
              fontSize: '14px'
            }}>
              <div style={{ marginBottom: '12px' }}>
                <strong>Rejection Reason:</strong> {kycStatus.rejectionReason}
              </div>
              <button
                className="btn-primary"
                onClick={handleResubmitKYC}
                style={{
                  background: '#DC2626',
                  padding: '8px 16px',
                  fontSize: '13px'
                }}
              >
                Re-submit for Review
              </button>
            </div>
          )}
        </div>

        {/* Two Column Layout: KYC Wizard (left) | Bank Account + History (right) */}
        <div className="kyc-main-content">
          {/* KYC Wizard - Show based on merchant's current status and level */}
          {(
            (kycStatus.status === 'APPROVED' && (kycStatus.level === 'LEVEL_1' || kycStatus.level === 'LEVEL_2')) ||
            (kycStatus.level === 'LEVEL_0') ||
            (kycStatus.status === 'NOT_STARTED') ||
            (kycStatus.status === 'IN_PROGRESS') ||
            (kycStatus.status === 'RESUBMISSION_REQUIRED')
          ) && (
            <div className="kyc-wizard-column">
              <KYCWizard />
            </div>
          )}

          {/* Right Column: Bank Account + History */}
          <div className="kyc-sidebar-column">
            {/* Bank Account Section */}
            <div className="bank-account-section">
              <h3>Settlement Bank Account</h3>
              <p className="bank-account-description">
                Your verified bank account where customer payments will be settled
              </p>

              {kycStatus.accountNumber ? (
                <>
                  <SecureBankAccountDisplay
                    accountNumber={kycStatus.accountNumber}
                    accountName={kycStatus.accountName}
                    bankName={kycStatus.bankName}
                    verified={kycStatus.accountVerified}
                    onConfirmPassword={handlePasswordConfirmation}
                    showActions={true}
                  />

                  <div className="bank-account-actions">
                    <button className="btn-secondary" onClick={() => setShowChangeBankModal(true)}>
                      <Building size={16} />
                      Change Bank Account
                    </button>
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <Building size={48} />
                  <p>No bank account added yet</p>
                  <button className="btn-primary" onClick={() => setShowChangeBankModal(true)} style={{ marginTop: '16px' }}>
                    Add Bank Account
                  </button>
                </div>
              )}
            </div>

            {/* Account History */}
            {accountHistory.length > 0 && (
              <div className="history-section">
                <h3>Bank Account History</h3>
                <div className="history-timeline">
                  {accountHistory.map((item, index) => (
                    <div key={item.id} className="history-item">
                      <div className={`history-dot ${item.verificationSuccess ? 'success' : 'failed'}`}></div>
                      <div className="history-content">
                        <div className="history-header">
                          <span className="history-title">{getChangeTypeLabel(item.changeType)}</span>
                          <span className="history-time">{formatDate(item.createdAt)}</span>
                        </div>
                        <div className="history-details">
                          {item.changeReason}
                        </div>
                        {item.newAccountNumber && (
                          <div className="history-account">
                            <Building size={14} />
                            <span>{item.newBankName} - ****{item.newAccountNumber.slice(-4)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Password Confirmation Modal */}
        <PasswordConfirmModal
          isOpen={showPasswordModal}
          onClose={handlePasswordModalClose}
          onConfirm={handlePasswordConfirm}
          title="Confirm Your Password"
          message="Please enter your password to view your full bank account number"
        />

        {/* Change Bank Account Modal */}
        <ChangeBankAccountModal
          isOpen={showChangeBankModal}
          onClose={() => setShowChangeBankModal(false)}
          onSuccess={handleChangeBankAccountSuccess}
          currentAccount={kycStatus.accountNumber ? {
            accountNumber: kycStatus.accountNumber,
            bankName: kycStatus.bankName
          } : null}
        />
      </div>
    </Shell>
  );
};

export default MerchantKYC;
